import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { ProductSpecCategory } from '@/gql/graphql';
import { EmptyState } from '@payaca/ambrosia';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppliancePicker from './detailPanel/components/AppliancePicker';
import EstimateItemEditor from './detailPanel/components/EstimateItemEditor';
import EstimateItemPicker from './detailPanel/components/EstimateItemPicker';
import DetailPanel from './detailPanel/DetailPanel';
import EstimateBuilderSidebarHeader from './EstimateBuilderSidebarHeader';
import EstimateFinancials from './EstimateFinancials';
import SendEstimateView from './SendEstimateView';

type EstimateBuilderSidebarProps = {
  projectId: string;
  estimateId: string;
  activeSectionId: string | null;
  onSetActiveSectionId: (id: string | null) => void;
};

type SidebarView =
  | { type: 'default' }
  | { type: 'addLineItem'; sectionId: string }
  | {
      type: 'addAppliance';
      category: ProductSpecCategory;
      sectionId: string;
    }
  | { type: 'editLineItem'; lineItemId: string }
  | { type: 'sendEstimate' }
  | { type: 'resendEstimate' };

const EstimateBuilderSidebar: FC<EstimateBuilderSidebarProps> = (props) => {
  const { projectId, estimateId, activeSectionId, onSetActiveSectionId } =
    props;

  const { estimate } = useGetProjectEstimate(projectId, estimateId);

  const [view, setView] = useState<SidebarView>({ type: 'default' });
  const history = useHistory();

  const goToDefault = () => setView({ type: 'default' });

  const isSent = estimate?.status === 'SENT';
  const isArchived = estimate?.status === 'ARCHIVED';
  // A sent Estimate defaults to a convert-focused overview so converting to a
  // Proposal is the primary action; it drops into the resend view when the
  // user picks Resend. A bounced Estimate is the exception: it defaults
  // straight to the resend view so the user can correct the recipient and
  // resend. The resend view owns its own top bar (reference, status badge,
  // recipient picker), so the sidebar header is suppressed there to avoid
  // stacking two bars.
  const showResendView =
    isSent &&
    !isArchived &&
    (view.type === 'resendEstimate' || !!estimate?.bouncedAt);

  if (view.type === 'addLineItem') {
    return (
      <EstimateItemPicker
        projectId={projectId}
        estimateId={estimateId}
        sectionId={view.sectionId}
        onClose={goToDefault}
      />
    );
  }

  if (view.type === 'addAppliance') {
    return (
      <AppliancePicker
        projectId={projectId}
        estimateId={estimateId}
        initialCategory={view.category}
        sectionId={view.sectionId}
        onClose={goToDefault}
      />
    );
  }

  if (view.type === 'editLineItem') {
    return (
      <EditLineItemView
        projectId={projectId}
        estimateId={estimateId}
        lineItemId={view.lineItemId}
        onBack={goToDefault}
      />
    );
  }

  // If the estimate becomes ARCHIVED while the user is in send mode (e.g. it
  // was archived from another tab and a cache refetch landed), drop back to
  // the default view so the main return renders the archived placeholder
  // instead of the send UI.
  if (view.type === 'sendEstimate' && !isArchived) {
    return (
      <SendEstimateView
        projectId={projectId}
        estimateId={estimateId}
        onBack={goToDefault}
        onSent={() => history.push(`/projects/${projectId}/estimates`)}
      />
    );
  }

  const showSidebarHeader = !showResendView;

  let builderContent = null;
  if (isArchived) {
    builderContent = (
      <EmptyState
        className="flex h-full flex-col items-center justify-center text-center"
        iconName="archive"
        text="This estimate is archived and edits aren't allowed. Unarchive to enable editing."
      />
    );
  } else if (showResendView) {
    builderContent = (
      <SendEstimateView
        projectId={projectId}
        estimateId={estimateId}
        // Offer a way back to the overview only when the user navigated
        // into resend from it; a bounced Estimate defaults here, so there
        // is no overview to return to.
        onBack={view.type === 'resendEstimate' ? goToDefault : undefined}
        backLabel="Back to overview"
        onSent={() => history.push(`/projects/${projectId}/estimates`)}
      />
    );
  } else if (!isSent) {
    builderContent = (
      <DetailPanel
        projectId={projectId}
        estimateId={estimateId}
        activeSectionId={activeSectionId}
        onSetActiveSectionId={onSetActiveSectionId}
        onAddLineItem={(sectionId) =>
          setView({ type: 'addLineItem', sectionId })
        }
        onEditLineItem={(lineItemId) =>
          setView({ type: 'editLineItem', lineItemId })
        }
        onAddAppliance={(category, sectionId) =>
          setView({ type: 'addAppliance', category, sectionId })
        }
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showSidebarHeader && (
        <EstimateBuilderSidebarHeader
          projectId={projectId}
          estimateId={estimateId}
          estimate={estimate}
          onEnterSendMode={() => setView({ type: 'sendEstimate' })}
          onResend={() => setView({ type: 'resendEstimate' })}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto flex flex-col">
        {!showResendView && (
          <EstimateFinancials projectId={projectId} estimateId={estimateId} />
        )}

        {builderContent}
      </div>
    </div>
  );
};

type EditLineItemViewProps = {
  projectId: string;
  estimateId: string;
  lineItemId: string;
  onBack: () => void;
};

const EditLineItemView: FC<EditLineItemViewProps> = (props) => {
  const { projectId, estimateId, lineItemId, onBack } = props;
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const lineItem = estimate?.sections
    .flatMap((s) => s.lineItems)
    .find((li) => li.id === lineItemId);

  // If the line item disappears from cache (deleted, or section refetch dropped
  // it), fall back to the default view.
  useEffect(() => {
    if (!estimate) return;
    if (!lineItem) onBack();
  }, [estimate, lineItem, onBack]);

  if (!lineItem) return null;

  return (
    <EstimateItemEditor
      key={lineItem.id}
      projectId={projectId}
      estimateId={estimateId}
      lineItem={lineItem}
      onBack={onBack}
    />
  );
};

export default EstimateBuilderSidebar;
